import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { useState } from '#app'
import { computed, watch } from 'vue'
import type { Ref } from 'vue'
import type { Language } from '~/resources/graphql/graphql'

export const UseLocalizationOrganizationFragment = graphql(`
    fragment UseLocalizationOrganizationFragment on Organization {
        locale
        currencyFormat
        startOfWeek
        enabledLanguages
    }
`)

type LocalizationState = {
    enabledLanguages: Language[]
    currencyFormat: string
    locale: string
    startOfWeek: number
}

type LocalizationOrganizationFragment = FragmentType<
    typeof UseLocalizationOrganizationFragment
>

const LOCALIZATION_STATE_KEY = 'cpx_localization'

function defaultLocalizationState(): LocalizationState {
    return {
        // Avoid guessing storefront languages before the organization context
        // loads so locale pickers only show org-configured options.
        enabledLanguages: [],
        currencyFormat: 'USD',
        locale: 'en-US',
        startOfWeek: 0
    }
}

function organizationLocalizationState(
    organization: LocalizationOrganizationFragment | null
): LocalizationState {
    if (organization === null) {
        return defaultLocalizationState()
    }

    const organizationData = getFragmentData(
        UseLocalizationOrganizationFragment,
        organization
    )

    return {
        enabledLanguages: organizationData.enabledLanguages,
        currencyFormat: organizationData.currencyFormat,
        locale: organizationData.locale,
        startOfWeek: organizationData.startOfWeek
    }
}

function useLocalizationState() {
    return useState<LocalizationState>(LOCALIZATION_STATE_KEY, () =>
        defaultLocalizationState()
    )
}

export function provideLocalizationOrganizationFragment(
    organization: Ref<LocalizationOrganizationFragment | null>
) {
    const localizationState = useLocalizationState()

    watch(
        organization,
        nextOrganization => {
            // Store serializable data in Nuxt state so SSR requests do not
            // share module-level organization fragments across tenants.
            localizationState.value =
                organizationLocalizationState(nextOrganization)
        },
        {
            flush: 'sync',
            immediate: true
        }
    )
}

export enum DateOrder {
    MonthDayYear,
    DayMonthYear,
    YearMonthDay
}

export function useLocalization() {
    const localeData = useLocalizationState()
    const currencyFormatter = computed(() => {
        return new Intl.NumberFormat(localeData.value.locale, {
            style: 'currency',
            currency: localeData.value.currencyFormat
        })
    })
    const currencyIsGBP = computed(
        () => localeData.value.currencyFormat === 'GBP'
    )
    const currencyIsEuro = computed(
        () => localeData.value.currencyFormat === 'EUR'
    )
    const currencyIsDollarSign = computed(
        () =>
            localeData.value.currencyFormat === 'USD' ||
            localeData.value.currencyFormat === 'CAD' ||
            localeData.value.currencyFormat === 'AUD' ||
            localeData.value.currencyFormat === 'SGD' ||
            localeData.value.currencyFormat === 'NZD' ||
            localeData.value.currencyFormat === 'MXN'
    )
    const currencyFormat = computed(() => localeData.value.currencyFormat)
    const locale = computed(() => localeData.value.locale)
    const availableLocales = computed(() => {
        // Keep GraphQL language enum mapping here so footer options match org settings.
        return localeData.value.enabledLanguages
            .map(language => {
                switch (language) {
                    case 'ENGLISH':
                        return { label: 'English', value: 'en-US' }
                    case 'SPANISH_MX':
                        return { label: 'Spanish', value: 'es-MX' }
                    case 'FRENCH':
                        return { label: 'French', value: 'fr-FR' }
                    case 'GERMAN':
                        return { label: 'German', value: 'de-DE' }
                    default:
                        return null
                }
            })
            .filter(
                (
                    option
                ): option is {
                    label: string
                    value: 'en-US' | 'es-MX' | 'fr-FR' | 'de-DE'
                } => option !== null
            )
    })
    const dateTimeFormatter = computed(() => {
        return new Intl.DateTimeFormat(localeData.value.locale, {
            dateStyle: 'long',
            timeStyle: 'short'
        })
    })
    const dateFormatter = computed(() => {
        return new Intl.DateTimeFormat(localeData.value.locale, {
            dateStyle: 'long'
        })
    })
    const datePartOrder = computed(() => {
        const parts = new Intl.DateTimeFormat(
            localeData.value.locale
        ).formatToParts()
        const filteredParts = parts.filter(part =>
            ['year', 'month', 'day'].includes(part.type)
        )

        // Use the locale's observed part order when Intl exposes all date parts.
        if (filteredParts.length === 3) {
            if (filteredParts[0].type === 'year') {
                return DateOrder.YearMonthDay
            }

            if (filteredParts[0].type === 'day') {
                return DateOrder.DayMonthYear
            }
        }

        // Fall back to US format when Intl does not expose a clear locale order.
        return DateOrder.MonthDayYear
    })
    const startOfWeek = computed(() => localeData.value.startOfWeek)

    return {
        currencyFormatter,
        dateTimeFormatter,
        dateFormatter,
        datePartOrder,
        startOfWeek,
        currencyIsGBP,
        currencyIsEuro,
        currencyIsDollarSign,
        currencyFormat,
        locale,
        availableLocales
    }
}
